
import { useState, useCallback } from 'react';
import { validateAndFetchArxivMetadata, ArxivMetadata } from '@/lib/api';
import { adminError } from '@/lib/utils/admin-logger';
import { ArxivLicense } from '@/types/license';
import { checkForDuplicateArxivPaper } from '@/lib/utils/duplicate-detection';
import { isArxivUrl } from '@/lib/utils/arxiv-utils';

export const useExternalUrlValidation = () => {
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [metadata, setMetadata] = useState<ArxivMetadata | null>(null);
  const [license, setLicense] = useState<ArxivLicense | undefined>(undefined);
  const [licenseCompliant, setLicenseCompliant] = useState<boolean | undefined>(undefined);
  const [licenseValidationError, setLicenseValidationError] = useState<string | undefined>(undefined);
  const [duplicateInfo, setDuplicateInfo] = useState<{
    isDuplicate: boolean;
    existingPaper?: {
      id: string;
      title: string;
      external_url: string;
    };
  } | null>(null);
  
  const validateUrl = useCallback(async (url: string) => {
    if (!url.trim()) {
      setValidationMessage('Please enter a URL');
      setValidationStatus('invalid');
      setValidationErrors(['URL is required']);
      return;
    }
    
    setValidationStatus('validating');
    setValidationMessage('Validating URL and checking license...');
    setValidationErrors([]);
    setLicense(undefined);
    setLicenseCompliant(undefined);
    setLicenseValidationError(undefined);
    setDuplicateInfo(null);
    
    try {
      // Check for duplicates first if it's an arXiv URL
      if (isArxivUrl(url)) {
        const duplicateResult = await checkForDuplicateArxivPaper(url);
        setDuplicateInfo(duplicateResult);
        
        if (duplicateResult.isDuplicate) {
          setValidationStatus('invalid');
          setValidationMessage('This paper already exists on the platform');
          setValidationErrors(['Paper already exists']);
          return;
        }
      }
      
      // Enable license checking when validating
      const result = await validateAndFetchArxivMetadata(url, true);
      
      if (result.isValid) {
        setValidationStatus('valid');
        setValidationMessage('Paper metadata fetched successfully');
        setMetadata(result.metadata);
        
        // Handle license information
        if (result.metadata?.license) {
          setLicense(result.metadata.license);
          setLicenseCompliant(result.licenseCompliant);
        }
        
        if (result.licenseValid === false && result.licenseCompliant === false) {
          setLicenseValidationError('License is not compliant with platform policies');
        }
      } else {
        setValidationStatus('invalid');
        setValidationMessage(result.errors?.[0] || 'Invalid URL');
        setValidationErrors(result.errors || []);
      }
    } catch (error) {
      adminError('Error validating URL:', error);
      setValidationStatus('invalid');
      setValidationMessage('Error validating URL');
      setValidationErrors(['An unexpected error occurred during validation']);
    }
  }, []);
  
  return {
    validateUrl,
    validationStatus,
    validationMessage,
    validationErrors,
    isValidating: validationStatus === 'validating',
    isValid: validationStatus === 'valid',
    metadata,
    license,
    licenseCompliant,
    licenseValidationError,
    duplicateInfo
  };
};
